import { ReactNode, useEffect, useRef } from "react";
import { Box, styled } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";

// Shared vocabulary for the Settlement surfaces, from the V9 Figma redesign:
//   https://www.figma.com/design/CMRDsNqVNtd1vcihsmTy3N/...?node-id=3001-3748  (panel + anchor wizard)
//   https://www.figma.com/design/CMRDsNqVNtd1vcihsmTy3N/...?node-id=3001-3751  (challenge + judge)
//
// Two surfaces, one anatomy. The panel is a white card holding grey blocks; the dialogs are a
// grey sheet holding white cards. Every callout is the same disc-plus-copy row either way, so
// `surface` picks the palette and the metrics rather than there being two components.
//
// Deliberately light-only with literal hex, matching the rest of this folder and the design:
// these are page-level surfaces the Figma pins to specific colours, not theme-following chrome.
// Token equivalents where they exist: #f2f2f2 = palette.back.infoCard, #d1efff =
// palette.back.searchComponent, #f3f3f3 = palette.background.paper, #3d3d3d = palette.divider,
// #121212 = palette.border.darker, #565656 = palette.text.disabled.

export const INK = "#000";
export const MUTED = "#888";
export const DISABLED_BG = "#999";
export const SHEET = "#f2f2f2";
export const ACCENT = "#d1efff";

// A callout's colouring. `info` is the default blue-disc note; the other three carry the
// meanings the old bespoke banners had (challenge amber, relay-progress blue, verdict red).
type Tone = "info" | "warning" | "error" | "progress";

interface ToneSpec {
  disc: string;
  discBorder?: string;
  icon: string;
  text: string;
  // Surface override — `warning`/`progress`/`error` tint the whole block, `info` does not.
  grey?: string;
  white?: string;
}

const TONES: Record<Tone, ToneSpec> = {
  info: { disc: ACCENT, icon: INK, text: INK },
  warning: { disc: "#FFE0B2", icon: "#B76E00", text: "#7A4100", grey: "#FFF4E5", white: "#FFF4E5" },
  // The drawn error glyph is a double ring: an outer stroke, a white gap, then a filled disc
  // with a knocked-out "i". Reproduced as a bordered transparent disc around InfoRounded
  // rather than shipping a one-off SVG. Copy stays black — the design carries the alarm in the
  // glyph alone, so the reasons underneath read as text rather than as more error chrome.
  error: { disc: "transparent", discBorder: "#E5342F", icon: "#E5342F", text: INK },
  progress: { disc: ACCENT, icon: "#1B5E9E", text: "#1B5E9E", grey: "#EAF3FF", white: "#EAF3FF" },
};

// Figma: 52px disc / 44px glyph on the panel, 38px disc / 30px glyph in the dialogs. The panel's
// row centres its disc against one or two lines; the dialogs' cards carry a paragraph and align
// the disc to its first line instead.
const METRICS = {
  grey: { disc: 52, glyph: 44, pad: "16px 20px", gap: "23px", radius: "20px", align: "center" },
  white: { disc: 38, glyph: 30, pad: "14px", gap: "13px", radius: "20px", align: "flex-start" },
};

// ── The "SETTLEMENT" card heading ────────────────────────────────────────────────────────────

export const SectionLabel = styled("p")`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 12.5px;
  line-height: 16.8px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${INK};
  margin: 0 0 16px;
`;

// ── Surfaces ─────────────────────────────────────────────────────────────────────────────────

// The content column of a redesigned dialog. The grey surface itself is painted by
// PopupDialog's paper (`surface="sheet"`), so that the sheet *is* the popup rather than a panel
// floating inside a white frame; this only owns the inset and the vertical rhythm.
export const DialogSheet = styled("div")`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// The blue record card on the judge screens — "Challenge under review" and its metadata.
export const DetailCard = styled("div")`
  background-color: ${ACCENT};
  border-radius: 20px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DetailTitle = styled("p")`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: ${INK};
  margin: 0;
`;

export const DetailBody = styled("p")`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22.4px;
  color: ${INK};
  margin: 0;
  white-space: pre-wrap;
`;

// Centred grey note under a dialog's buttons. Kept from the pre-redesign dialogs — the Figma
// draws one on the judge screen and omits the rest, which reads as not-drawn, not removed.
export const Footnote = styled("p")`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${MUTED};
  text-align: center;
  margin: 0;
`;

export const ErrorNote = styled("p")`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #d32f2f;
  margin: 0;
`;

// ── Callout ──────────────────────────────────────────────────────────────────────────────────

export interface InfoCalloutProps {
  children: ReactNode;
  tone?: Tone;
  // "grey" = a grey block inside the white panel card; "white" = a white card on a grey sheet.
  surface?: "grey" | "white";
  // Replaces the ⓘ glyph — e.g. a gavel for a challenge, a spinner for a relayed transaction.
  icon?: ReactNode;
  className?: string;
}

export const InfoCallout = ({ children, tone = "info", surface = "grey", icon, className }: InfoCalloutProps) => {
  const t = TONES[tone];
  const m = METRICS[surface];
  const background = (surface === "grey" ? t.grey ?? SHEET : t.white ?? "#fff") as string;
  const Glyph = tone === "error" ? InfoRoundedIcon : InfoOutlinedIcon;

  return (
    <Box
      className={className}
      sx={{
        display: "flex",
        alignItems: m.align,
        gap: m.gap,
        backgroundColor: background,
        borderRadius: m.radius,
        padding: m.pad,
        color: t.text,
        // Phones get the disc above nothing to gain, so keep the row but tighten the gutter.
        "@media (max-width: 560px)": { gap: "14px", alignItems: "flex-start" },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: m.disc,
          height: m.disc,
          borderRadius: "50%",
          backgroundColor: t.disc,
          border: t.discBorder ? `2px solid ${t.discBorder}` : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        {icon ?? <Glyph sx={{ fontSize: tone === "error" ? m.glyph * 0.72 : m.glyph, color: t.icon }} />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
};

// The sentence inside a callout. Inter 14/18, with `strong` picking up the drawn bold lead-in.
export const CalloutText = styled("p")`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  margin: 0;
  color: inherit;

  strong {
    font-weight: 700;
  }
`;

// A second, quieter line in the same callout — used only for the states the Figma doesn't draw,
// where something extra has to be said without earning a block of its own.
export const CalloutSubText = styled(CalloutText)`
  margin-top: 4px;
  opacity: 0.75;
`;

// A value set in mono inside running copy — the countdown in "anchor … within 1D 23H 59M 47S".
export const MonoValue = styled("span")`
  font-family: "IBM Plex Mono", monospace;
  font-weight: 700;
  text-transform: uppercase;
`;

// ── Monospace metadata ───────────────────────────────────────────────────────────────────────

export interface MetaItem {
  label: string;
  value: string;
  // Renders the value in the muted colour too — the judge card's timestamp, which the design
  // sets entirely in grey rather than as a labelled value.
  muted?: boolean;
}

// The "SOLD TO 0xB1AC…5A35   ESCROW 3500.0 USD" row: mono, uppercase, grey label + black value.
export const MonoMeta = ({
  items,
  muted = MUTED,
  stacked,
}: {
  items: MetaItem[];
  muted?: string;
  stacked?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: stacked ? "column" : "row",
      flexWrap: stacked ? "nowrap" : "wrap",
      columnGap: "18px",
      rowGap: "2px",
      marginTop: "7px",
      fontFamily: "'IBM Plex Mono', monospace",
      fontWeight: 700,
      fontSize: "12px",
      lineHeight: "15.6px",
      textTransform: "uppercase",
    }}
  >
    {items.map((item) => (
      <span key={`${item.label}-${item.value}`}>
        {item.label && <span style={{ color: muted }}>{item.label} </span>}
        <span style={{ color: item.muted ? muted : INK }}>{item.value}</span>
      </span>
    ))}
  </Box>
);

// The centred "DEED HASH 0x…" line above the panel's primary action.
export const HashLine = ({ label, value }: MetaItem) => (
  <Box
    sx={{
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: "14px",
      lineHeight: "18px",
      textAlign: "center",
      wordBreak: "break-all",
    }}
  >
    <span style={{ color: MUTED, textTransform: "uppercase" }}>{label} </span>
    <span style={{ color: INK, fontWeight: 700 }}>{value}</span>
  </Box>
);

// ── Segmented choice ─────────────────────────────────────────────────────────────────────────

export interface SegmentedChoiceProps<T extends string> {
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
}

// Track inset and the gap between options, in px. The pill derives its geometry from these
// rather than measuring the buttons.
const TRACK_PAD = 2;
const TRACK_GAP = 2;

// The grey track with a black pill, drawn for Dismiss / Uphold. It *selects* rather than acts:
// the verdict is submitted by the button below it, so the irreversible one keeps a second beat.
//
// The pill is a single element that slides between options (transitions.dev "Tabs sliding";
// the `--tabs-*` timing tunables are installed on `:root` in App.tsx). Every option is
// `flex: 1`, so the pill's width is a fixed fraction of the track and only `transform` ever
// tweens — no offsetLeft/offsetWidth reads, no resize listener, and no width animation, in
// keeping with the compositor-only rule the rest of the app's motion follows.
export const SegmentedChoice = <T extends string>({ options, value, onChange, disabled }: SegmentedChoiceProps<T>) => {
  const count = options.length;
  const activeIndex = options.findIndex((option) => option.value === value);
  // Where the pill was on the previous render. With nothing selected before (the dialogs open
  // with `value: null`) the pill fades in where it lands instead of sliding in from the first
  // option; on the way back to null it fades out in place.
  const lastIndex = useRef(activeIndex);
  const restingIndex = activeIndex >= 0 ? activeIndex : Math.max(lastIndex.current, 0);
  const slide = activeIndex >= 0 && lastIndex.current >= 0;
  useEffect(() => {
    lastIndex.current = activeIndex;
  });

  return (
    <Box
      role="radiogroup"
      sx={{
        position: "relative",
        display: "flex",
        gap: `${TRACK_GAP}px`,
        padding: `${TRACK_PAD}px`,
        height: 44,
        borderRadius: "20px",
        backgroundColor: DISABLED_BG,
        opacity: disabled ? 0.6 : 1,
        boxSizing: "border-box",
      }}
    >
      {/* The pill sits under the labels (z 0 vs 1). Percentages inside translate are of the
          pill's own width, so "one option over" is one pill width plus one gap. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: `${TRACK_PAD}px`,
          left: `${TRACK_PAD}px`,
          height: 40,
          width: `calc((100% - ${TRACK_PAD * 2 + TRACK_GAP * (count - 1)}px) / ${count})`,
          borderRadius: "20px",
          backgroundColor: INK,
          transform: `translate3d(calc(${restingIndex} * (100% + ${TRACK_GAP}px)), 0, 0)`,
          opacity: activeIndex >= 0 ? 1 : 0,
          transition: slide
            ? "transform var(--tabs-dur) var(--tabs-ease), opacity var(--tabs-dur) var(--tabs-ease)"
            : "opacity var(--tabs-dur) var(--tabs-ease)",
          pointerEvents: "none",
          zIndex: 0,
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      />
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            sx={{
              position: "relative",
              zIndex: 1,
              flex: 1,
              minWidth: 0,
              height: 40,
              border: "none",
              borderRadius: "20px",
              cursor: disabled ? "default" : "pointer",
              backgroundColor: "transparent",
              color: selected ? "#fff" : INK,
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "24.5px",
              // The label turns over on the pill's clock so the two arrive together.
              transition: "color var(--tabs-dur) var(--tabs-ease), background-color var(--tabs-dur) var(--tabs-ease)",
              "&:hover": { backgroundColor: selected ? "transparent" : "rgba(0, 0, 0, 0.08)" },
              "&:focus-visible": { outline: "2px solid #000", outlineOffset: "2px" },
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
};

// The Figma's disabled primary is a flat grey pill, not PopupActionButton's translucent black.
// Applied at call sites via sx so the shared button keeps its own disabled treatment elsewhere.
export const figmaButtonSx = {
  height: 44,
  "&.Mui-disabled": {
    backgroundColor: DISABLED_BG,
    color: "rgba(255, 255, 255, 0.6)",
  },
} as const;
